/**
 * Load only the active template font pair on public pages.
 * Avoids the multi-family Google Fonts CSS that blocked Slow 4G (~all weights
 * of Bricolage, Playfair, Fraunces, …) when only 1–2 families are used.
 */

const loadedFamilies = new Set<string>();

/** Map design font display names → Google Fonts CSS2 family tokens. */
const GOOGLE_FAMILY: Record<string, string> = {
  Inter: "Inter:wght@400;500;600;700",
  Poppins: "Poppins:wght@400;500;600;700",
  "Nunito Sans": "Nunito+Sans:wght@400;500;600;700",
  "Space Grotesk": "Space+Grotesk:wght@400;500;600;700",
  Lora: "Lora:wght@400;500;600;700",
  "Space Mono": "Space+Mono:wght@400;700",
  "Bricolage Grotesque": "Bricolage+Grotesque:opsz,wght@12..96,400;500;600;700",
  Fraunces: "Fraunces:opsz,wght@9..144,400;500;600;700",
  Marcellus: "Marcellus:wght@400",
  Oswald: "Oswald:wght@400;500;600;700",
  "Playfair Display": "Playfair+Display:wght@400;500;600;700",
  Gloock: "Gloock:wght@400",
  "DM Serif Display": "DM+Serif+Display:wght@400",
  Syne: "Syne:wght@400;500;600;700",
  "Instrument Serif": "Instrument+Serif:wght@400",
  "Work Sans": "Work+Sans:wght@400;500;600",
  "Cormorant Garamond": "Cormorant+Garamond:wght@400;500;600;700",
  Jost: "Jost:wght@400;500;600",
  "DM Sans": "DM+Sans:opsz,wght@9..40,400;500;600;700",
};

function normalizeFontName(raw: string): string {
  return String(raw || "")
    .replace(/['"]/g, "")
    .split(",")[0]
    .trim();
}

function injectStylesheet(href: string, key: string) {
  if (typeof document === "undefined") return;
  if (document.getElementById(key)) return;
  const el = document.createElement("link");
  el.id = key;
  el.rel = "stylesheet";
  el.href = href;
  // Non-blocking: activate after load so public Slow 4G LCP stays intact.
  // Pair with display=swap so faces still apply after the first paint window.
  el.media = "print";
  el.onload = () => {
    el.media = "all";
  };
  document.head.appendChild(el);
}

/**
 * Ensure Google Fonts for the resolved display/body faces are present.
 * No-ops for system / self-hosted faces (Futura, Finder, Hagrid).
 */
export function ensurePublicTemplateFonts(
  primaryFont?: string | null,
  secondaryFont?: string | null,
): void {
  if (typeof document === "undefined") return;
  const names = [primaryFont, secondaryFont]
    .map((f) => normalizeFontName(String(f || "")))
    .filter(Boolean);
  const families: string[] = [];
  for (const name of names) {
    const token = GOOGLE_FAMILY[name];
    if (!token || loadedFamilies.has(token)) continue;
    loadedFamilies.add(token);
    families.push(token);
  }
  if (families.length === 0) return;
  const href = `https://fonts.googleapis.com/css2?${families
    .map((f) => `family=${f}`)
    .join("&")}&display=swap`;
  injectStylesheet(href, `browsable-tpl-fonts-${families.join("-").slice(0, 80)}`);
}
